import { TFunction } from 'i18next'
import get from 'lodash/get'
import isString from 'lodash/isString'

import { ARCHIVED, ENABLED, PAUSED } from 'const/resourceStates'
import { ProductAd } from 'types'

import { ProductAdField } from './localTypes'

export const PRODUCT_AD_LOOKUP_CHUNK_SIZE = 50
export const PRODUCT_AD_LOOKUP_PAGE_SIZE = 250
export const PRODUCT_AD_CREATE_BATCH_SIZE = 10

export interface ClassifiedProductAds {
    toCreate: ProductAdField[]
    alreadyEnabled: ProductAdField[]
    paused: ProductAd[]
    archived: ProductAd[]
}

export interface ProductAdSubmitCounts {
    created: number
    enabled: number
    alreadyEnabled: number
    archived: number
    alreadyInAdGroup: number
    failed: number
}

export function productAdMatchKey(
    identifier: string,
    adGroupId: string
): string {
    return `${adGroupId}::${identifier}`
}

export function incomingProductAdIdentifier(product: ProductAdField): string {
    return product.asin.trim()
}

export function existingProductAdIdentifier(
    productAd: ProductAd,
    isSeller: boolean
): string | null {
    const value = isSeller ? productAd.sku : productAd.asin
    if (!value) {
        return null
    }
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
}

export function classifyIncomingProductAds(
    products: ProductAdField[],
    existing: ProductAd[],
    isSeller: boolean
): ClassifiedProductAds {
    const existingByKey = new Map<string, ProductAd>()
    existing.forEach((productAd) => {
        const identifier = existingProductAdIdentifier(productAd, isSeller)
        if (!identifier) {
            return
        }
        const key = productAdMatchKey(identifier, productAd.ad_group_id)
        if (!existingByKey.has(key)) {
            existingByKey.set(key, productAd)
        }
    })

    const seenIncoming = new Set<string>()
    const toCreate: ProductAdField[] = []
    const alreadyEnabled: ProductAdField[] = []
    const paused: ProductAd[] = []
    const archived: ProductAd[] = []
    const pausedIds = new Set<string>()
    const archivedIds = new Set<string>()

    products.forEach((product) => {
        const identifier = incomingProductAdIdentifier(product)
        if (!identifier || !product.adGroupId) {
            return
        }
        const key = productAdMatchKey(identifier, product.adGroupId)
        if (seenIncoming.has(key)) {
            return
        }
        seenIncoming.add(key)

        const match = existingByKey.get(key)
        if (!match) {
            toCreate.push({ ...product, asin: identifier })
            return
        }
        if (match.state === ENABLED) {
            alreadyEnabled.push(product)
            return
        }
        if (match.state === PAUSED && !pausedIds.has(match.id)) {
            paused.push(match)
            pausedIds.add(match.id)
            return
        }
        if (match.state === ARCHIVED && !archivedIds.has(match.id)) {
            archived.push(match)
            archivedIds.add(match.id)
        }
    })

    return { toCreate, alreadyEnabled, paused, archived }
}

export function isDuplicateProductAdError(message: string): boolean {
    return /already exists|duplicate/i.test(message)
}

export function extractCerebroErrorMessage(data: unknown): string | null {
    const first = get(data, ['0'])
    const description = get(first, ['description'])
    if (isString(description)) {
        return description
    }
    if (isString(first)) {
        return first
    }
    const detail = get(data, ['detail'])
    if (isString(detail)) {
        return detail
    }
    return null
}

export async function runInBatches<T, R>(
    items: readonly T[],
    batchSize: number,
    iteratee: (item: T) => Promise<R>
): Promise<R[]> {
    const results: R[] = []
    for (let index = 0; index < items.length; index += batchSize) {
        const batch = items.slice(index, index + batchSize)
        results.push(...(await Promise.all(batch.map(iteratee))))
    }
    return results
}

function identifierTypeLabel(
    t: TFunction,
    isSeller: boolean,
    count: number
): string {
    return isSeller
        ? t('campaigns:createProductAd.skus', 'SKU', { count })
        : t('campaigns:createProductAd.asins', 'ASIN', { count })
}

export function formatProductAdSubmitSummary(
    counts: ProductAdSubmitCounts,
    t: TFunction,
    isSeller: boolean
): string {
    const parts: string[] = []
    if (counts.created > 0) {
        parts.push(
            t(
                'campaigns:createProductAd.created',
                'Created {{count}} {{type}}.',
                {
                    count: counts.created,
                    type: identifierTypeLabel(t, isSeller, counts.created),
                }
            )
        )
        parts.push(
            t(
                'campaigns:createProductAd.createdVisibilityNote',
                'They are live on Amazon; this tab may take longer to update.'
            )
        )
    }
    if (counts.enabled > 0) {
        parts.push(
            t(
                'campaigns:createProductAd.enabledPaused',
                'Enabled {{count}} paused {{type}}.',
                {
                    count: counts.enabled,
                    type: identifierTypeLabel(t, isSeller, counts.enabled),
                }
            )
        )
    }
    if (counts.alreadyEnabled > 0) {
        parts.push(
            t(
                'campaigns:createProductAd.alreadyEnabled',
                '{{count}} {{type}} was already enabled.',
                {
                    count: counts.alreadyEnabled,
                    type: identifierTypeLabel(
                        t,
                        isSeller,
                        counts.alreadyEnabled
                    ),
                }
            )
        )
    }
    if (counts.archived > 0) {
        parts.push(
            t(
                'campaigns:createProductAd.archivedSkipped',
                '{{count}} archived {{type}} was skipped.',
                {
                    count: counts.archived,
                    type: identifierTypeLabel(t, isSeller, counts.archived),
                }
            )
        )
    }
    if (counts.alreadyInAdGroup > 0) {
        parts.push(
            t(
                'campaigns:createProductAd.alreadyInAdGroup',
                '{{count}} {{type}} was already in this ad group.',
                {
                    count: counts.alreadyInAdGroup,
                    type: identifierTypeLabel(
                        t,
                        isSeller,
                        counts.alreadyInAdGroup
                    ),
                }
            )
        )
    }
    if (counts.failed > 0) {
        parts.push(
            t(
                'campaigns:createProductAd.failed',
                '{{count}} {{type}} could not be added.',
                {
                    count: counts.failed,
                    type: identifierTypeLabel(t, isSeller, counts.failed),
                }
            )
        )
    }
    return parts.join(' ')
}

export function shouldCloseProductAdModal(
    counts: ProductAdSubmitCounts
): boolean {
    if (counts.created + counts.enabled > 0) {
        return true
    }
    if (counts.failed > 0) {
        return false
    }
    return counts.alreadyEnabled + counts.archived + counts.alreadyInAdGroup > 0
}

export function hasSuccessfulProductAdSubmit(
    counts: ProductAdSubmitCounts
): boolean {
    return (
        counts.created +
            counts.enabled +
            counts.alreadyEnabled +
            counts.archived +
            counts.alreadyInAdGroup >
        0
    )
}

export function productAdSubmitToastType(
    counts: ProductAdSubmitCounts,
    summary: string
): 'success' | 'warning' | 'error' | null {
    if (!summary) {
        return null
    }
    const succeeded = hasSuccessfulProductAdSubmit(counts)
    if (succeeded && counts.failed > 0) {
        return 'warning'
    }
    if (succeeded) {
        return 'success'
    }
    if (counts.failed > 0) {
        return 'error'
    }
    return null
}
